import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  userId: string;
}

const COLORS = ["#02857E", "#34d399", "#f59e0b", "#a78bfa", "#60a5fa", "#f472b6"];
const EMPTY_THRESHOLD = 3;

function ChartCard({
  title,
  empty,
  emptyHint,
  children,
}: {
  title: string;
  empty: boolean;
  emptyHint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h4 className="text-sm font-bold text-foreground">{title}</h4>
      <div className="mt-3 h-48">
        {empty ? (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            {emptyHint || "Not enough data yet"}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

/**
 * Section 4 — Insights grid (4 recharts visualizations).
 * Each chart degrades to a "Not enough data yet" message under EMPTY_THRESHOLD records.
 */
export function InsightsGrid({ userId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["shipper-insights", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const since30 = new Date(Date.now() - 30 * 86_400_000).toISOString();
      const since16w = new Date(Date.now() - 16 * 7 * 86_400_000).toISOString();

      const { data: loads } = await supabase
        .from("direct_loads")
        .select("id, equipment_type, awarded_bid_id, awarded_at, created_at")
        .eq("created_by", userId)
        .gte("created_at", since16w);
      const loadList = loads || [];

      // Loads per week — last 8 weeks + previous 8 weeks.
      const weekKey = (d: Date) => {
        const monday = new Date(d);
        const day = (monday.getDay() + 6) % 7;
        monday.setDate(monday.getDate() - day);
        monday.setHours(0, 0, 0, 0);
        return monday.toISOString().slice(0, 10);
      };
      const weeksMap = new Map<string, number>();
      loadList.forEach((l: any) => {
        const k = weekKey(new Date(l.created_at));
        weeksMap.set(k, (weeksMap.get(k) || 0) + 1);
      });
      const allWeeks: { week: string; count: number }[] = [];
      const now = new Date();
      for (let i = 15; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i * 7);
        const k = weekKey(d);
        allWeeks.push({ week: k.slice(5), count: weeksMap.get(k) || 0 });
      }
      const last8 = allWeeks.slice(8).map((w, i) => ({
        week: w.week,
        current: w.count,
        previous: allWeeks[i].count,
      }));

      // Spend by equipment (awarded loads, last 30d).
      const awarded30 = loadList.filter(
        (l: any) => l.awarded_at && l.awarded_at >= since30 && l.awarded_bid_id,
      );
      const awardedBidIds = awarded30.map((l: any) => l.awarded_bid_id).filter(Boolean);
      const { data: awardedBids } = awardedBidIds.length
        ? await supabase
            .from("direct_bids")
            .select("id, bid_amount, broker_id")
            .in("id", awardedBidIds)
        : { data: [] as any[] };
      const bidById = new Map((awardedBids || []).map((b: any) => [b.id, b]));
      const equipMap = new Map<string, number>();
      const brokerMap = new Map<string, { count: number; spend: number }>();
      awarded30.forEach((l: any) => {
        const bid: any = bidById.get(l.awarded_bid_id);
        const amount = Number(bid?.bid_amount || 0);
        const eq = l.equipment_type || "Other";
        equipMap.set(eq, (equipMap.get(eq) || 0) + amount);
        if (bid?.broker_id) {
          const prev = brokerMap.get(bid.broker_id) || { count: 0, spend: 0 };
          brokerMap.set(bid.broker_id, { count: prev.count + 1, spend: prev.spend + amount });
        }
      });
      const equipmentData = Array.from(equipMap.entries()).map(([name, value]) => ({ name, value }));

      // Top brokers (last 30d).
      const topBrokerIds = Array.from(brokerMap.keys());
      const { data: brokerProfiles } = topBrokerIds.length
        ? await supabase
            .from("profiles")
            .select("id, first_name, last_name, email")
            .in("id", topBrokerIds)
        : { data: [] as any[] };
      const nameById = new Map(
        (brokerProfiles || []).map((p: any) => [
          p.id,
          [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "Broker",
        ]),
      );
      const topBrokers = Array.from(brokerMap.entries())
        .map(([id, v]) => ({ name: nameById.get(id) || "Broker", count: v.count, spend: v.spend }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Avg rate vs market (last 30d): per-day rate computed from awarded loads.
      // Market baseline currently optional — render hint when unavailable.
      const days = 30;
      const todayKey = (d: Date) => d.toISOString().slice(0, 10);
      const dayMap = new Map<string, { sum: number; cnt: number }>();
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        dayMap.set(todayKey(d), { sum: 0, cnt: 0 });
      }
      awarded30.forEach((l: any) => {
        const k = (l.awarded_at ?? "").toString().slice(0, 10);
        if (!k) return;
        const bid: any = bidById.get(l.awarded_bid_id);
        if (dayMap.has(k) && bid?.bid_amount) {
          const cur = dayMap.get(k)!;
          cur.sum += Number(bid.bid_amount);
          cur.cnt += 1;
        }
      });
      const rateSeries = Array.from(dayMap.entries()).map(([day, v]) => ({
        day: day.slice(5),
        yours: v.cnt ? Math.round(v.sum / v.cnt) : null,
      }));

      return {
        weekly: last8,
        equipment: equipmentData,
        topBrokers,
        rateSeries,
        awardedCount: awarded30.length,
      };
    },
  });

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-64 rounded-lg border bg-card p-4">
            <div className="h-4 w-32 rounded bg-muted animate-pulse" />
            <div className="mt-4 h-40 rounded bg-muted/40 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  const totalLoads = data.weekly.reduce((s, w) => s + w.current, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <ChartCard
        title="Loads Per Week (last 8 weeks)"
        empty={totalLoads < EMPTY_THRESHOLD}
        emptyHint="Post a few loads to see your weekly volume trend"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.weekly}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="previous" fill="hsl(var(--muted-foreground))" opacity={0.4} />
            <Bar dataKey="current" fill="#02857E" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Spend by Equipment (last 30 days)"
        empty={data.equipment.length === 0}
        emptyHint="Award loads to see spend breakdown by equipment"
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data.equipment}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={70}
              paddingAngle={2}
            >
              {data.equipment.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Top Brokers by Volume (30d)"
        empty={data.topBrokers.length === 0}
        emptyHint="Award loads to surface your top broker partners"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.topBrokers} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
            <Tooltip
              formatter={(v: number, key: string) =>
                key === "spend" ? `$${v.toLocaleString()}` : `${v} loads`
              }
            />
            <Bar dataKey="count" fill="#02857E" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Your Avg Award Rate (30d)"
        empty={data.awardedCount < EMPTY_THRESHOLD}
        emptyHint="Market baseline currently unavailable — your daily avg appears once you award 3+ loads"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.rateSeries}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="day" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: number) => (v ? `$${v.toLocaleString()}` : "—")} />
            <Line type="monotone" dataKey="yours" stroke="#02857E" dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
